// Centralized mock data service with relationships

export interface Client {
  id: number
  number: string
  name: string
  status: 'Prospect' | 'Attivo' | 'Non attivo'
  zone: string
  email: string
  phone: string
  referent: string
  type: string[]
  clientOf?: string[]
  safetyReferent?: string
  employeesCount?: number
}

export interface Worker {
  id: number
  clientId: number
  name: string
  role: string
  trainingStatus: 'In regola' | 'In scadenza' | 'Scaduto'
  healthStatus: 'Idoneo' | 'Non idoneo' | 'Parzialmente idoneo'
}

export interface Contract {
  id: number
  number: string
  client: string
  product: string
  status:
    | 'proposal'
    | 'pending'
    | 'urgent'
    | 'accepted'
    | 'rejected'
    | 'revoked'
  amount: string
  expiry: string
}

// Generate realistic data
const generateClients = (count: number): Client[] => {
  const safetyReferents = ['Alessio', 'Lorenzo', 'Gian Franco', 'Tiziano']
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    number: `CLI-${String(i + 1).padStart(4, '0')}`,
    name:
      i % 3 === 0
        ? `Azienda ${i} SRL`
        : i % 3 === 1
          ? `Studio ${i} Associati`
          : `Impresa ${i} SPA`,
    status: i % 10 === 0 ? 'Non attivo' : i % 5 === 0 ? 'Prospect' : 'Attivo',
    zone: ['Arezzo', 'Valdarno', 'Casentino', 'Firenze'][i % 4],
    email: `info@azienda${i}.it`,
    phone: `0575 ${100000 + i}`,
    referent: `Referente ${i}`,
    type: i % 2 === 0 ? ['Seform'] : ['Sef Studio', 'Seform'],
    clientOf: i % 2 === 0 ? ['seform'] : ['sef_studio', 'seform'],
    safetyReferent: safetyReferents[i % 4],
    employeesCount: Math.floor(Math.random() * 50) + 5,
  }))
}

const generateWorkers = (
  clients: Client[],
  countPerClient: number,
): Worker[] => {
  let workers: Worker[] = []
  let idCounter = 1

  clients.forEach((client) => {
    for (let i = 0; i < countPerClient; i++) {
      workers.push({
        id: idCounter++,
        clientId: client.id,
        name: `Lavoratore ${client.id}-${i}`,
        role: i % 3 === 0 ? 'Operaio' : 'Impiegato',
        trainingStatus:
          Math.random() > 0.8
            ? 'Scaduto'
            : Math.random() > 0.6
              ? 'In scadenza'
              : 'In regola',
        healthStatus: Math.random() > 0.9 ? 'Non idoneo' : 'Idoneo',
      })
    }
  })
  return workers
}

const generateContracts = (clients: Client[], count: number): Contract[] => {
  const products = [
    'Consulenza Sicurezza',
    'HACCP Base',
    'Privacy GDPR',
    'Formazione Lavoratori',
    'DVR',
  ]
  const statuses: Contract['status'][] = [
    'proposal',
    'pending',
    'urgent',
    'accepted',
    'rejected',
    'revoked',
  ]

  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    number: `C-2024/${String(i + 1).padStart(3, '0')}`,
    client: clients[i % clients.length].name,
    product: products[i % products.length],
    status: statuses[i % statuses.length],
    amount: `€ ${(Math.random() * 5000 + 1000).toFixed(2)}`,
    expiry: `${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}/${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}/2024`,
  }))
}

// Singleton data store
class MockDataService {
  private clients: Client[] = []
  private workers: Worker[] = []
  private contracts: Contract[] = []

  constructor() {
    this.clients = generateClients(50)
    this.workers = generateWorkers(this.clients, 5) // ~250 workers
    this.contracts = generateContracts(this.clients, 100)
  }

  getClients() {
    return this.clients
  }
  getClient(id: number) {
    return this.clients.find((c) => c.id === id)
  }

  getWorkers() {
    return this.workers
  }
  getWorkersByClient(clientId: number) {
    return this.workers.filter((w) => w.clientId === clientId)
  }

  getContracts() {
    return this.contracts
  }

  // Add other getters as needed
}

export const mockData = new MockDataService()
